<template>
    <div class="strategy-stats">
        <h2 class="strategy-stats-title">{{ $t('titles_JournalStats.strategy') }}</h2>

        <v-row class="strategy-stats-charts">
            <v-col cols="12" md="6" class="strategy-chart-col">
                <h3 class="strategy-chart-title">{{ $t('profitLosses.title') }}</h3>
                <p class="strategy-chart-subtitle">{{ $t('profitLosses.subtitle') }}</p>
                <apexchart
                    type="bar"
                    :height="strategyChartHeight"
                    :options="strategyChartOptions"
                    :series="strategyChartSeries"
                />
            </v-col>

            <v-col cols="12" md="6" class="strategy-chart-col">
                <div class="strategy-chart-header-row">
                    <div>
                        <h3 class="strategy-chart-title">{{ $t('strategyStats.byStrategyAsset.title') }}</h3>
                        <p class="strategy-chart-subtitle">{{ $t('strategyStats.byStrategyAsset.subtitle') }}</p>
                    </div>
                    <v-select
                        v-model="selectedStrategy"
                        :items="strategyOptions"
                        item-text="label"
                        item-value="value"
                        dense
                        outlined
                        hide-details
                        class="strategy-select"
                        @change="buildStrategyAssetChart"
                    />
                </div>
                <apexchart
                    type="bar"
                    :height="strategyAssetChartHeight"
                    :options="strategyAssetChartOptions"
                    :series="strategyAssetChartSeries"
                />
            </v-col>
        </v-row>

        <JournalInsightBar :insight="insight" />
    </div>
</template>

<script>
import JournalInsightBar from '@/views/Journal/components/JournalInsightBar';

const CHART_GREEN = '#005931';
const CHART_RED = '#D00808';
const CHART_COLORS = [CHART_GREEN, CHART_RED];
const BAR_HEIGHT = 44;

function buildProfitLossMap(trades, getKey) {
    const map = {};

    trades.forEach((trade) => {
        const key = getKey(trade);
        if (!key) {
            return;
        }

        if (!(key in map)) {
            map[key] = { profits: 0, losses: 0, count: 0 };
        }

        map[key].count += 1;
        if (trade.netProfit > 0) {
            map[key].profits += trade.netProfit;
        } else if (trade.netProfit < 0) {
            map[key].losses += trade.netProfit;
        }
    });

    return map;
}

function sortEntriesByNetProfit(entries) {
    return entries.sort((a, b) => {
        const netA = a.profits + a.losses;
        const netB = b.profits + b.losses;
        return netB - netA;
    });
}

function createEmptyDivergingBarOptions() {
    return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false } },
        colors: CHART_COLORS,
        fill: {
            type: 'solid',
            colors: CHART_COLORS,
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '70%',
            },
        },
        dataLabels: { enabled: false },
        stroke: { width: 1, colors: ['#fff'] },
        legend: { show: false },
        grid: {
            borderColor: '#f3f4f6',
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: false } },
            padding: { left: 8, right: 16 },
        },
        annotations: {
            xaxis: [{
                x: 0,
                borderColor: '#d1d5db',
                strokeDashArray: 0,
            }],
        },
        xaxis: {
            min: -1,
            max: 1,
            tickAmount: 4,
            categories: [],
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#ababab',
                    fontSize: '13px',
                    fontFamily: 'Avenir, sans-serif',
                },
            },
        },
        tooltip: { shared: true, intersect: false },
    };
}

export default {
    name: "StrategyStats",
    components: { JournalInsightBar },
    props: {
        // AI insight for this section, already resolved by JournalStats (null = nothing to show).
        insight: { type: Object, default: null },
    },
    data() {
        return {
            selectedStrategy: null,
            strategyOptions: [],
            strategyChartSeries: [{ name: 'Profit', data: [] }, { name: 'Losses', data: [] }],
            strategyChartOptions: createEmptyDivergingBarOptions(),
            strategyAssetChartSeries: [{ name: 'Profit', data: [] }, { name: 'Losses', data: [] }],
            strategyAssetChartOptions: createEmptyDivergingBarOptions(),
        };
    },
    computed: {
        strategyChartHeight() {
            return this.getChartHeight(this.strategyChartSeries);
        },
        strategyAssetChartHeight() {
            return this.getChartHeight(this.strategyAssetChartSeries);
        },
    },
    beforeMount() {
        this.setStats();
    },
    watch: {
        '$store.state.book.trades': function () {
            this.setStats();
        },
        '$i18n.locale': function () {
            this.setStats();
        },
    },
    methods: {
        setStats() {
            this.buildStrategyChart();
            this.buildStrategyOptions();
            this.buildStrategyAssetChart();
        },
        getStrategyName(trade) {
            if (trade.strategy && trade.strategy.name) {
                return trade.strategy.name;
            }
            return this.$t('strategyStats.none');
        },
        getChartHeight(series) {
            const count = series[0] && series[0].data ? series[0].data.length : 0;
            return Math.max(280, count * BAR_HEIGHT + 60);
        },
        buildStrategyOptions() {
            const trades = this.$store.state.book.trades.slice();
            const map = buildProfitLossMap(trades, (trade) => this.getStrategyName(trade));
            const entries = sortEntriesByNetProfit(Object.entries(map).map(([name, stats]) => ({
                name,
                ...stats,
            })));

            this.strategyOptions = entries.map((entry) => ({
                label: entry.name,
                value: entry.name,
            }));

            if (!this.strategyOptions.length) {
                this.selectedStrategy = null;
                return;
            }

            const stillValid = this.strategyOptions.some((option) => option.value === this.selectedStrategy);
            if (!stillValid) {
                const mostUsed = [...entries].sort((a, b) => b.count - a.count)[0];
                this.selectedStrategy = mostUsed ? mostUsed.name : this.strategyOptions[0].value;
            }
        },
        buildStrategyChart() {
            const trades = this.$store.state.book.trades.slice();
            const map = buildProfitLossMap(trades, (trade) => this.getStrategyName(trade));
            const entries = sortEntriesByNetProfit(Object.entries(map).map(([name, stats]) => ({
                name,
                profits: stats.profits,
                losses: stats.losses,
            })));

            this.applyChartData(
                entries,
                (entry) => entry.name,
                'strategyChartSeries',
                'strategyChartOptions'
            );
        },
        buildStrategyAssetChart() {
            if (!this.selectedStrategy) {
                this.strategyAssetChartSeries = [];
                this.strategyAssetChartOptions = this.createChartOptions([], 0);
                return;
            }

            const trades = this.$store.state.book.trades.slice().filter((trade) => {
                return this.getStrategyName(trade) === this.selectedStrategy;
            });

            const map = buildProfitLossMap(trades, (trade) => {
                if (!trade.asset || !trade.asset.ticker) {
                    return null;
                }
                return `${this.selectedStrategy} & ${trade.asset.ticker}`;
            });

            const entries = sortEntriesByNetProfit(Object.entries(map).map(([name, stats]) => ({
                name,
                profits: stats.profits,
                losses: stats.losses,
            })));

            this.applyChartData(
                entries,
                (entry) => entry.name,
                'strategyAssetChartSeries',
                'strategyAssetChartOptions'
            );
        },
        applyChartData(entries, getLabel, seriesKey, optionsKey) {
            const labels = entries.map(getLabel);
            const profitValues = entries.map((entry) => entry.profits);
            const lossValues = entries.map((entry) => entry.losses);
            const maxValue = entries.reduce((max, entry) => {
                return Math.max(max, entry.profits, Math.abs(entry.losses));
            }, 0);

            this[seriesKey] = [
                { name: this.$t('strategyStats.winners'), data: profitValues },
                { name: this.$t('strategyStats.losers'), data: lossValues },
            ];
            this[optionsKey] = this.createChartOptions(labels, maxValue);
        },
        createChartOptions(categories, maxValue) {
            const limit = Math.max(maxValue * 1.1, 1);

            return {
                theme: {
                    mode: this.$store.state.currentTheme == 'dark' ? 'dark' : 'light',
                },
                noData: {
                    text: 'No data for the current filter',
                    align: 'center',
                    verticalAlign: 'middle',
                    style: {
                        color: '#111111',
                        fontSize: '21px',
                        fontFamily: 'Ubuntu, sans-serif',
                    },
                },
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: { show: false },
                },
                colors: CHART_COLORS,
                fill: {
                    type: 'solid',
                    colors: CHART_COLORS,
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '70%',
                    },
                },
                dataLabels: { enabled: false },
                stroke: {
                    width: 1,
                    colors: ['#fff'],
                },
                legend: { show: false },
                grid: {
                    borderColor: '#f3f4f6',
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: false } },
                    padding: { left: 8, right: 16 },
                },
                annotations: {
                    xaxis: [{
                        x: 0,
                        borderColor: '#d1d5db',
                        strokeDashArray: 0,
                    }],
                },
                xaxis: {
                    min: -limit,
                    max: limit,
                    tickAmount: 4,
                    categories,
                    labels: { show: false },
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                },
                yaxis: {
                    labels: {
                        style: {
                            colors: '#ababab',
                            fontSize: '13px',
                            fontFamily: 'Avenir, sans-serif',
                        },
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: (val) => `$${Math.abs(val).toFixed(2)}`,
                    },
                },
            };
        },
        dayKey(dayIndex) {
            const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            return keys[dayIndex];
        },    },
};
</script>

<style lang="scss" scoped>
.strategy-stats {
    .strategy-stats-title {
        margin: 0 0 24px;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 28px;
        color: var(--v-darkGrey-base);
    }

    .strategy-stats-charts {
        margin-bottom: 20px;
    }

    .strategy-chart-col {
        padding-top: 0;
    }

    .strategy-chart-header-row {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 4px;
    }

    .strategy-chart-title {
        margin: 0 0 4px;
        font-family: 'Avenir', sans-serif;
        font-weight: 700;
        font-size: 16px;
        color: var(--v-mainBlack-base);
    }

    .strategy-chart-subtitle {
        margin: 0 0 16px;
        font-family: 'Avenir', sans-serif;
        font-size: 14px;
        line-height: 1.4;
        color: #ababab;
    }

    .strategy-select {
        max-width: 180px;
        flex-shrink: 0;
        margin-top: 0;

        ::v-deep .v-input__control {
            min-height: 36px !important;
        }

        ::v-deep .v-input__slot {
            min-height: 36px !important;
            border-color: #ababab !important;
        }

        ::v-deep fieldset {
            border-color: #ababab !important;
        }
    }

}
</style>
